import AddCoupon from "features/subscriptions/components/ManageSubscription/AddCoupon";
import AlertBanners from "features/subscriptions/components/ManageSubscription/AlertBanners";
import AppliedCoupon from "features/subscriptions/components/ManageSubscription/AppliedCoupon";
import CreditsApplied from "features/subscriptions/components/ManageSubscription/CreditsApplied";
import { CurrentSubscriptionEstimate } from "features/subscriptions/components/ManageSubscription/CurrentSubscriptionEstimate";
import CustomerCredits from "features/subscriptions/components/ManageSubscription/CustomerCredits";
import { EstimateRow } from "features/subscriptions/components/ManageSubscription/EstimateRow";
import NewPrice from "features/subscriptions/components/ManageSubscription/NewPrice";
import QuantitySelection from "features/subscriptions/components/ManageSubscription/QuantitySelection";
import ToPayNow from "features/subscriptions/components/ManageSubscription/ToPayNow";
import SubscriptionPlan from "features/subscriptions/components/SubscriptionPlan";
import { useManageSubscription } from "features/subscriptions/context/ManageSubscriptionContext";
import useManageSubscriptionEstimate from "features/subscriptions/hooks/useManageSubscriptionEstimate";
import usePricingOptions from "features/subscriptions/hooks/usePricingOptions";
import useSubscriptionCoupon from "features/subscriptions/hooks/useSubscriptionCoupon";
import type { InvoiceEstimate } from "features/subscriptions/types";
import { parseEstimate } from "features/subscriptions/utils";

import DottedSeparator from "common/components/DottedSeparator";
import Skeleton from "common/components/ui/Skeleton";
import usePromotions from "common/datahooks/usePromotions";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";

import AgencySeatPricing from "./AgencySeatPricing";
import DesktopLayout from "./DesktopLayout";
import MobileLayout from "./MobileLayout";
import PricingPeriodSelector from "./PricingPeriodSelector";
import UserAndRentalSeatPricing from "./UserAndRentalSeatPricing";

import type { JSX } from "react";

interface ManageSubscriptionDetailsProps {
  couponAction: string;
  setCouponAction: (setCouponAction: string | "remove") => void;
  isPaymentPreviousStep: boolean;
  paymentSourceComponent: JSX.Element;
  errorMessageComponent: JSX.Element;
  subscriptionActionButton: JSX.Element;
}

export default function ManageSubscriptionDetails({
  couponAction,
  setCouponAction,
  isPaymentPreviousStep,
  paymentSourceComponent,
  subscriptionActionButton,
  errorMessageComponent,
}: ManageSubscriptionDetailsProps) {
  const isDesktop = useTwBreakpoint("lg");
  const {
    initialState,
    seatQuantity,
    setPricingPeriodInfo,
    pricing,
    subscriptionType,
    hasExistingSubscription,
    hasActiveSubscription,
    isUnchanged,
    isCancellation,
    rentalOrderSubscription,
  } = useManageSubscription();

  const { agencyPlan } = usePromotions();

  const { pricingOptions } = usePricingOptions(
    subscriptionType,
    hasExistingSubscription ? initialState.subscription : undefined,
  );

  const { updateOrCreateEstimate, toPayNowEstimate, isLoadingEstimate } =
    useManageSubscriptionEstimate({ couponAction });

  const { subscriptionCoupon } = useSubscriptionCoupon(
    hasActiveSubscription &&
      hasExistingSubscription &&
      initialState.subscription.id,
    subscriptionType === "agency",
  );

  let assignedLicenseCount = 0;

  if (agencyPlan) {
    const { license_count, unassigned_license_count } = agencyPlan;
    assignedLicenseCount = license_count - unassigned_license_count;
  }

  const currentEstimateComponent = hasExistingSubscription &&
    initialState.subscription.status !== "cancelled" && (
      <CurrentSubscriptionEstimate
        subscriptionType={subscriptionType}
        quantity={initialState.seats}
        subscriptionId={initialState.subscription.id}
        pricingPeriodInfo={initialState.subscription.item_price}
      />
    );

  const subscriptionPlanComponent = (
    <SubscriptionPlan
      plan={subscriptionType}
      isTrial={
        hasExistingSubscription &&
        initialState.subscription.status === "in_trial"
      }
    />
  );

  const pricingPeriodSelectionComponent = (
    <PricingPeriodSelector
      pricingOptions={pricingOptions}
      value={pricing?.plan}
      setValue={setPricingPeriodInfo}
    />
  );

  const quantitySelectionComponent = <QuantitySelection />;

  const bannersComponent = (
    <AlertBanners assignedLicenseCount={assignedLicenseCount} />
  );

  let newSeatCountTooltipText =
    subscriptionType === "rental"
      ? "The number of Avatars included in your subscription. Avatars come with their own seats, they do not use seats from other sources"
      : "The number of seats included in your subscription";

  if (hasActiveSubscription) {
    newSeatCountTooltipText =
      subscriptionType === "rental"
        ? "The updated number of Avatars included in your subscription, if you proceed with the update. Avatars come with their own seats, they do not use seats from other sources"
        : "The updated number of seats included in your subscription, if you proceed with the update";
  }

  const newSeatCountComponent = (
    <div className="lg:mt-0.5">
      <EstimateRow
        label={`${
          hasActiveSubscription ? "New no." : "No."
        } of ${subscriptionType === "rental" ? "Avatars" : "seats"}`}
        tooltipText={newSeatCountTooltipText}
        valueComponent={<span className="text-md-bold">{seatQuantity}</span>}
        isLoading={false}
      />
    </div>
  );

  let pricingComponent = (
    <Skeleton className="h-4 w-16 lg:w-8 max-lg:mx-auto lg:mr-auto" />
  );

  if (pricing) {
    pricingComponent =
      subscriptionType === "agency" ? (
        <AgencySeatPricing pricing={pricing} />
      ) : (
        <UserAndRentalSeatPricing
          pricing={pricing}
          isRental={subscriptionType === "rental"}
        />
      );
  }

  //rental orders show the estimate on downgrades too - the rental estimate
  //endpoint returns a next_invoice_estimate for scheduled changes
  const shouldShowValue = rentalOrderSubscription
    ? !isUnchanged || !!couponAction
    : (!isUnchanged || !hasActiveSubscription || !!couponAction) &&
      !isCancellation;

  const newPriceComponent = (
    <NewPrice
      subscriptionType={subscriptionType}
      pricingPeriodInfo={pricing?.plan}
      estimate={shouldShowValue && updateOrCreateEstimate}
      isLoading={isLoadingEstimate}
    />
  );
  const toPayNowComponent = (
    <ToPayNow
      estimate={shouldShowValue ? toPayNowEstimate : undefined}
      isLoading={isLoadingEstimate}
    />
  );

  let couponComponent = !isCancellation && (
    <AddCoupon setCouponId={(couponId) => setCouponAction(couponId)} />
  );
  if (isLoadingEstimate) {
    couponComponent = (
      <>
        <DottedSeparator />
        <Skeleton className="w-20 h-5 ml-2.5" />
        <DottedSeparator />
      </>
    );
  }

  if (couponAction !== "remove") {
    let props: { estimate: InvoiceEstimate } | { couponId: string } | undefined;

    if (updateOrCreateEstimate) {
      const { coupon } = parseEstimate(updateOrCreateEstimate);
      if (coupon) {
        props = { estimate: updateOrCreateEstimate };
      } else if (toPayNowEstimate?.invoice_estimate) {
        const { coupon } = parseEstimate(toPayNowEstimate.invoice_estimate);
        if (coupon) {
          props = { estimate: toPayNowEstimate.invoice_estimate };
        }
      }
    }
    if (!props && couponAction) {
      props = { couponId: couponAction };
    }

    if (props) {
      couponComponent = (
        <AppliedCoupon
          {...props}
          onRemove={() =>
            setCouponAction(subscriptionCoupon ? "remove" : undefined)
          }
        />
      );
    }
  }

  const creditsComponent = <CustomerCredits />;

  const creditsAppliedComponent = shouldShowValue && (
    <CreditsApplied
      estimate={toPayNowEstimate?.invoice_estimate}
      isLoading={isLoadingEstimate}
    />
  );

  if (isDesktop) {
    const estimateComponents = (
      <>
        <div className="flex flex-col gap-y-2.5">
          {currentEstimateComponent}
          {newSeatCountComponent}
          {newPriceComponent}
        </div>
        {couponComponent}
        {creditsAppliedComponent}
        {toPayNowComponent}
        {errorMessageComponent}
        {subscriptionActionButton}
        {bannersComponent}
      </>
    );
    return (
      <DesktopLayout
        subscriptionPlanComponent={subscriptionPlanComponent}
        pricingPeriodSelectionComponent={pricingPeriodSelectionComponent}
        quantitySelectionComponent={quantitySelectionComponent}
        pricingComponent={pricingComponent}
        paymentSourceComponent={paymentSourceComponent}
        creditsComponent={creditsComponent}
        estimateComponents={estimateComponents}
      />
    );
  }

  const estimateComponents = (
    <>
      {newSeatCountComponent}
      {newPriceComponent}
      {bannersComponent}
      {couponComponent}
      {creditsAppliedComponent}
      {toPayNowComponent}
    </>
  );

  return (
    <MobileLayout
      subscriptionType={subscriptionType}
      subscriptionPlanComponent={subscriptionPlanComponent}
      pricingPeriodSelectionComponent={pricingPeriodSelectionComponent}
      currentEstimateComponent={currentEstimateComponent}
      quantitySelectionComponent={quantitySelectionComponent}
      pricingComponent={pricingComponent}
      paymentSourceComponent={paymentSourceComponent}
      creditsComponent={creditsComponent}
      subscriptionActionButton={subscriptionActionButton}
      estimateComponents={estimateComponents}
      errorMessageComponent={errorMessageComponent}
      isPaymentPreviousStep={isPaymentPreviousStep}
    />
  );
}
